import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Workbook } from 'exceljs';
import * as fs from 'file-saver';

@Injectable({
  providedIn: 'root',
})
export class ExportExcelService {
  private datePipe = new DatePipe('en-US');

  /**
   * @ayush
   * Download Excel with Optional Dropdowns
   *
   * Example usage:
   * let excelData = {
   *   fileName: 'Product Upload',
   *   sheetName: 'products',
   *   headers: ['SKU', 'Product Name', ...],
   *   body: [], // optional data rows
   *   multipleSheets: true/false,
   *   sheets: [
   *     {
   *       name: 'Categories',
   *       key: 'category_name',
   *       hasDropdown: true,
   *       column: 'C',
   *       body: [{category_name: 'Gift Card'}, ...]
   *     }
   *   ]
   * }
   */
  DownloadCommonExcelWithOptionalDropDown(excelData: any) {
    const finalHeaders = excelData.headers;
    let workbook = new Workbook();

    // CSV Export (Only supports single sheet, no dropdowns or formatting)
    const format = excelData.format || 'xlsx';
    if (format == 'csv') {
      let worksheet = workbook.addWorksheet(excelData.sheetName);
      worksheet.addRow(finalHeaders);
      if (excelData.body && excelData.body.length > 0) {
        excelData.body.forEach((d: any) => {
          worksheet.addRow(d);
        });
      }

      workbook.csv.writeBuffer().then(data => {
        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        fs.saveAs(
          blob,
          `${excelData.fileName} ` + '' + this.datePipe.transform(new Date(), 'medium') + '.csv'
        );
      });

      return;
    }

    // Normal XLSX logic continues below...
    let worksheet = workbook.addWorksheet(excelData.sheetName, {
      pageSetup: {
        horizontalCentered: true,
        verticalCentered: true,
        paperSize: 9,
        orientation: 'portrait',
        margins: {
          left: 0.3149606,
          right: 0.3149606,
          top: 0.3543307,
          bottom: 0.3543307,
          header: 0.3149606,
          footer: 0.3149606,
        },
      },
    });

    // Add Header Row
    let headerRow = worksheet.addRow(finalHeaders);

    // Cell Style : Fill and Border
    headerRow.eachCell((cell: any, number: any) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF00' },
        bgColor: { argb: 'FF0000FF' },
      };
      cell.font = {
        bold: true,
        name: 'Calibri',
        size: 8,
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };

      cell.protection = {
        locked: false,
      };
    });

    worksheet.columns = finalHeaders.map((header: string) => ({ header, width: 26 }));

    if (excelData.body && excelData.body.length > 0) {
      excelData.body.forEach((d: any) => {
        let row = worksheet.addRow(d);
        row.font = {
          bold: false,
          name: 'Arial',
          size: 8,
        };
        row.alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
      });
    }

    // Only if multipleSheets is true this code is executed
    if (excelData.multipleSheets == true && excelData.sheets) {
      excelData.sheets.forEach((sheet: any) => {
        let commonWorksheet = workbook.addWorksheet(sheet.name, {
          pageSetup: {
            horizontalCentered: true,
            verticalCentered: true,
            paperSize: 9,
            orientation: 'portrait',
            margins: {
              left: 0.3149606,
              right: 0.3149606,
              top: 0.3543307,
              bottom: 0.3543307,
              header: 0.3149606,
              footer: 0.3149606,
            },
          },
        });

        // Add Header Row
        let commonHeaderRow = commonWorksheet.addRow([sheet.name]);

        // Cell Style : Fill and Border
        commonHeaderRow.eachCell((cell: any, number: any) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '9999FF' },
            bgColor: { argb: 'FF0000FF' },
          };
          cell.font = {
            bold: true,
            name: 'Calibri',
            size: 8,
          };
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });

        commonWorksheet.columns = [{ header: sheet.name, key: sheet.key, width: 26 }];

        sheet.body.forEach((d: any) => {
          let row = commonWorksheet.addRow(d);
          row.font = {
            name: 'Calibri',
            size: 8,
          };
          row.alignment = {
            vertical: 'middle',
            horizontal: 'center',
          };
        });

        if (sheet.hasDropdown) {
          // Wrap sheet name in single quotes to handle special characters (hyphen, space, etc.)
          let Formulae = [`'${sheet.name}'!$A$2:$A$${sheet.body.length + 1}`];

          for (var i = 2; i < 50; i++) {
            worksheet.getCell(`${sheet.column}${i}`).dataValidation = {
              type: 'list',
              allowBlank: false,
              formulae: Formulae,
              errorStyle: 'error',
              errorTitle: 'choose a status',
              error: 'please select',
              showErrorMessage: true,
            };
          }
        }
      });
    }

    // Add Data and Conditional Formatting
    workbook.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      fs.saveAs(
        blob,
        `${excelData.fileName} ` + '' + this.datePipe.transform(new Date(), 'medium') + '.xlsx'
      );
    });
  }

  /**
   * Export Excel with data rows
   * @param headers Array of header column names
   * @param data Array of data objects to export
   * @param excel_name Name for the Excel file
   */
  exportExcelWithData(headers: string[], data: any[], excel_name: string) {
    if (headers.length === 0 || data.length === 0) {
      console.error('Headers or data is empty.');
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(excel_name, {
      pageSetup: {
        horizontalCentered: true,
        verticalCentered: true,
        paperSize: 9,
        orientation: 'portrait',
        margins: {
          left: 0.3149606,
          right: 0.3149606,
          top: 0.3543307,
          bottom: 0.3543307,
          header: 0.3149606,
          footer: 0.3149606,
        },
      },
    });

    // Add Header Row
    let headerRow = worksheet.addRow(headers);

    // Cell Style : Fill and Border
    headerRow.eachCell((cell: any, number: any) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF00' },
        bgColor: { argb: 'FF0000FF' },
      };
      cell.font = {
        bold: true,
        name: 'Calibri',
        size: 8,
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Fill Data Rows
    data.forEach(item => {
      let row = worksheet.addRow(Object.values(item));
      row.eachCell((cell: any) => {
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
      });
    });

    worksheet.columns.forEach((column: any) => {
      column.width = 26;
    });

    // Add Data and Conditional Formatting
    workbook.xlsx
      .writeBuffer()
      .then(buffer => {
        let blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const timestamp =
          this.datePipe.transform(new Date(), 'medium')?.replace(/[ :]/g, '_') || '';
        const filename = `${excel_name}_${timestamp}.xlsx`;

        fs.saveAs(blob, filename);
      })
      .catch(error => {
        console.error('Error exporting Excel:', error);
      });
  }
}
